import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Upload, Download, AlertCircle, Stamp, RotateCcw, Bold, Italic, Grid3X3 } from 'lucide-react';

type WatermarkPosition = 'TL' | 'TC' | 'TR' | 'ML' | 'MC' | 'MR' | 'BL' | 'BC' | 'BR' | 'tile';
type OutputFormat = 'png' | 'jpg';

const FONTS = ['Arial', 'Georgia', 'Courier New', 'Times New Roman', 'Verdana', 'Impact'];

const POSITION_LABELS: Record<WatermarkPosition, string> = {
  TL: 'TL', TC: 'TC', TR: 'TR',
  ML: 'ML', MC: 'MC', MR: 'MR',
  BL: 'BL', BC: 'BC', BR: 'BR',
  tile: 'Tile',
};

const GRID_POSITIONS: WatermarkPosition[][] = [
  ['TL', 'TC', 'TR'],
  ['ML', 'MC', 'MR'],
  ['BL', 'BC', 'BR'],
];

const ImageWatermark: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [originalPreview, setOriginalPreview] = useState<string | null>(null);
  const [watermarkText, setWatermarkText] = useState('Watermark');
  const [fontFamily, setFontFamily] = useState('Arial');
  const [fontSize, setFontSize] = useState(36);
  const [color, setColor] = useState('#ffffff');
  const [opacity, setOpacity] = useState(50);
  const [isBold, setIsBold] = useState(true);
  const [isItalic, setIsItalic] = useState(false);
  const [position, setPosition] = useState<WatermarkPosition>('tile');
  const [rotation, setRotation] = useState(0);
  const [tileSpacing, setTileSpacing] = useState(100);
  const [outputFormat, setOutputFormat] = useState<OutputFormat>('png');
  const [jpgQuality, setJpgQuality] = useState(92);
  const [error, setError] = useState<string | null>(null);
  const [imgElement, setImgElement] = useState<HTMLImageElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);

  const handleFileSelect = useCallback((selectedFiles: FileList | null) => {
    if (!selectedFiles || selectedFiles.length === 0) return;
    const f = selectedFiles[0];
    if (!f.type.startsWith('image/')) {
      setError('Please select a valid image file.');
      return;
    }
    setFile(f);
    setError(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      const url = e.target?.result as string;
      setOriginalPreview(url);
      const img = new window.Image();
      img.onload = () => setImgElement(img);
      img.onerror = () => setError('Failed to load image.');
      img.src = url;
    };
    reader.readAsDataURL(f);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    handleFileSelect(e.dataTransfer.files);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const drawWatermark = useCallback((canvas: HTMLCanvasElement, img: HTMLImageElement) => {
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(img, 0, 0);

    // Scale font size relative to image
    const scaledFontSize = Math.round(fontSize * (img.naturalWidth / 800));
    const fontWeight = isBold ? 'bold' : 'normal';
    const fontStyle = isItalic ? 'italic' : 'normal';
    ctx.font = `${fontStyle} ${fontWeight} ${scaledFontSize}px ${fontFamily}`;
    ctx.fillStyle = color;
    ctx.globalAlpha = opacity / 100;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    try {
      if (position === 'tile') {
        const diagonal = Math.sqrt(canvas.width ** 2 + canvas.height ** 2);
        const spacingX = ctx.measureText(watermarkText).width + tileSpacing;
        const spacingY = scaledFontSize + tileSpacing;

        ctx.save();
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((-45 * Math.PI) / 180);
        for (let y = -diagonal; y < diagonal; y += spacingY) {
          for (let x = -diagonal; x < diagonal; x += spacingX) {
            ctx.save();
            ctx.translate(x, y);
            ctx.rotate((rotation * Math.PI) / 180);
            ctx.fillText(watermarkText, 0, 0);
            ctx.restore();
          }
        }
        ctx.restore();
      } else {
        const pad = scaledFontSize * 1.5;
        const posMap: Record<string, [number, number]> = {
          TL: [pad, pad],
          TC: [canvas.width / 2, pad],
          TR: [canvas.width - pad, pad],
          ML: [pad, canvas.height / 2],
          MC: [canvas.width / 2, canvas.height / 2],
          MR: [canvas.width - pad, canvas.height / 2],
          BL: [pad, canvas.height - pad],
          BC: [canvas.width / 2, canvas.height - pad],
          BR: [canvas.width - pad, canvas.height - pad],
        };

        const [x, y] = posMap[position] || [canvas.width / 2, canvas.height / 2];

        if (position.endsWith('L')) ctx.textAlign = 'left';
        else if (position.endsWith('R')) ctx.textAlign = 'right';
        else ctx.textAlign = 'center';

        ctx.save();
        ctx.translate(x, y);
        ctx.rotate((rotation * Math.PI) / 180);
        ctx.fillText(watermarkText, 0, 0);
        ctx.restore();
      }
    } catch (err) {
      // Silently handle drawing errors
    }

    ctx.globalAlpha = 1;
  }, [watermarkText, fontFamily, fontSize, color, opacity, isBold, isItalic, position, rotation, tileSpacing]);

  // Live preview
  useEffect(() => {
    if (!imgElement || !previewCanvasRef.current) return;
    drawWatermark(previewCanvasRef.current, imgElement);
  }, [imgElement, drawWatermark]);

  const downloadResult = useCallback(() => {
    if (!imgElement || !file) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    drawWatermark(canvas, imgElement);

    const mimeType = outputFormat === 'jpg' ? 'image/jpeg' : 'image/png';
    const quality = outputFormat === 'jpg' ? jpgQuality / 100 : undefined;

    canvas.toBlob((blob) => {
      if (!blob) return;
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      const name = file.name.replace(/\.[^.]+$/, '');
      const ext = outputFormat === 'jpg' ? 'jpg' : 'png';
      link.download = `${name}_watermarked.${ext}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    }, mimeType, quality);
  }, [imgElement, file, drawWatermark, outputFormat, jpgQuality]);

  const reset = useCallback(() => {
    setFile(null);
    setOriginalPreview(null);
    setImgElement(null);
    setWatermarkText('Watermark');
    setFontFamily('Arial');
    setFontSize(36);
    setColor('#ffffff');
    setOpacity(50);
    setIsBold(true);
    setIsItalic(false);
    setPosition('tile');
    setRotation(0);
    setTileSpacing(100);
    setOutputFormat('png');
    setJpgQuality(92);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  return (
    <div className="space-y-6">
      <canvas ref={canvasRef} className="hidden" />

      {/* Header */}
      <div className="text-center">
        <div className="flex justify-center mb-4">
          <div className="p-3 bg-emerald-100 rounded-full">
            <Stamp className="w-8 h-8 text-emerald-600" />
          </div>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Image Watermark</h2>
        <p className="text-gray-600">
          Add customizable text watermarks with font, position, and tiling options.
        </p>
      </div>

      {/* Upload */}
      {!file && (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-emerald-400 transition-colors cursor-pointer"
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Drop an image here or click to select
          </h3>
          <p className="text-gray-600">Supports PNG, JPG, WebP and more</p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={(e) => handleFileSelect(e.target.files)}
            className="hidden"
          />
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
          <AlertCircle className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {file && imgElement && (
        <div className="space-y-4">
          {/* Text & Font Controls */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Watermark Text</label>
                <input
                  type="text"
                  value={watermarkText}
                  onChange={(e) => setWatermarkText(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  placeholder="Enter watermark text"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Font Family</label>
                <select
                  value={fontFamily}
                  onChange={(e) => setFontFamily(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                >
                  {FONTS.map((f) => (
                    <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Bold / Italic / Color */}
            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => setIsBold((v) => !v)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors border-2 ${
                  isBold
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                    : 'border-gray-200 bg-white text-gray-600 hover:border-emerald-300'
                }`}
              >
                <Bold className="w-4 h-4" />
                Bold
              </button>
              <button
                onClick={() => setIsItalic((v) => !v)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors border-2 ${
                  isItalic
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                    : 'border-gray-200 bg-white text-gray-600 hover:border-emerald-300'
                }`}
              >
                <Italic className="w-4 h-4" />
                Italic
              </button>
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">Color</label>
                <input
                  type="color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="w-10 h-10 rounded-lg cursor-pointer border border-gray-300"
                />
              </div>
            </div>
          </div>

          {/* Font Size & Opacity */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-3">
            <div>
              <div className="flex justify-between items-center mb-1">
                <label className="text-sm font-medium text-gray-700">Font Size</label>
                <span className="text-sm font-bold text-gray-900">{fontSize}px</span>
              </div>
              <input
                type="range"
                min={12}
                max={120}
                value={fontSize}
                onChange={(e) => setFontSize(parseInt(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-emerald-600"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-0.5">
                <span>12px</span>
                <span>120px</span>
              </div>
            </div>
            <div>
              <div className="flex justify-between items-center mb-1">
                <label className="text-sm font-medium text-gray-700">Opacity</label>
                <span className="text-sm font-bold text-gray-900">{opacity}%</span>
              </div>
              <input
                type="range"
                min={5}
                max={100}
                value={opacity}
                onChange={(e) => setOpacity(parseInt(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-emerald-600"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-0.5">
                <span>5%</span>
                <span>100%</span>
              </div>
            </div>
            <div>
              <div className="flex justify-between items-center mb-1">
                <label className="text-sm font-medium text-gray-700">Text Rotation</label>
                <span className="text-sm font-bold text-gray-900">{rotation} deg</span>
              </div>
              <input
                type="range"
                min={0}
                max={360}
                value={rotation}
                onChange={(e) => setRotation(parseInt(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-emerald-600"
              />
            </div>
          </div>

          {/* Position Grid */}
          <div className="bg-gray-50 rounded-xl p-4">
            <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center gap-1.5">
              <Grid3X3 className="w-4 h-4" />
              Position
            </label>
            <div className="flex flex-wrap items-start gap-4">
              {/* 3x3 Grid */}
              <div className="inline-grid grid-cols-3 gap-1.5">
                {GRID_POSITIONS.map((row, ri) =>
                  row.map((pos) => (
                    <button
                      key={pos}
                      onClick={() => setPosition(pos)}
                      className={`w-12 h-12 rounded-lg text-xs font-medium transition-colors border-2 ${
                        position === pos
                          ? 'border-emerald-500 bg-emerald-100 text-emerald-700'
                          : 'border-gray-200 bg-white text-gray-500 hover:border-emerald-300'
                      }`}
                    >
                      {POSITION_LABELS[pos]}
                    </button>
                  ))
                )}
              </div>
              {/* Tile button */}
              <button
                onClick={() => setPosition('tile')}
                className={`flex flex-col items-center justify-center gap-1 px-6 py-3 rounded-lg text-sm font-medium transition-colors border-2 h-[104px] ${
                  position === 'tile'
                    ? 'border-emerald-500 bg-emerald-100 text-emerald-700'
                    : 'border-gray-200 bg-white text-gray-500 hover:border-emerald-300'
                }`}
              >
                <Grid3X3 className="w-5 h-5" />
                Diagonal Tile
              </button>
            </div>

            {/* Tile Spacing */}
            {position === 'tile' && (
              <div className="mt-4">
                <div className="flex justify-between items-center mb-1">
                  <label className="text-sm font-medium text-gray-700">Tile Spacing</label>
                  <span className="text-sm font-bold text-gray-900">{tileSpacing}px</span>
                </div>
                <input
                  type="range"
                  min={20}
                  max={300}
                  value={tileSpacing}
                  onChange={(e) => setTileSpacing(parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-emerald-600"
                />
              </div>
            )}
          </div>

          {/* Output Format */}
          <div className="bg-gray-50 rounded-xl p-4">
            <label className="block text-sm font-medium text-gray-700 mb-3">Output Format</label>
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex gap-2">
                {(['png', 'jpg'] as OutputFormat[]).map((fmt) => (
                  <button
                    key={fmt}
                    onClick={() => setOutputFormat(fmt)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium uppercase transition-colors ${
                      outputFormat === fmt
                        ? 'bg-emerald-600 text-white'
                        : 'bg-white border border-gray-200 text-gray-700 hover:border-emerald-300'
                    }`}
                  >
                    {fmt}
                  </button>
                ))}
              </div>
              {outputFormat === 'jpg' && (
                <div className="flex-1 min-w-[200px]">
                  <div className="flex justify-between items-center mb-1">
                    <label className="text-xs font-medium text-gray-600">JPG Quality</label>
                    <span className="text-xs font-bold text-gray-900">{jpgQuality}%</span>
                  </div>
                  <input
                    type="range"
                    min={10}
                    max={100}
                    value={jpgQuality}
                    onChange={(e) => setJpgQuality(parseInt(e.target.value))}
                    className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-emerald-600"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Preview */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-2">Live Preview</h3>
            <div className="bg-gray-100 rounded-xl overflow-hidden flex items-center justify-center min-h-[200px]">
              <canvas ref={previewCanvasRef} className="max-w-full max-h-[500px] object-contain" />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3">
            <button
              onClick={downloadResult}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-emerald-600 text-white font-medium rounded-xl hover:bg-emerald-700 transition-colors"
            >
              <Download className="w-5 h-5" />
              Download Watermarked Image
            </button>
            <button
              onClick={reset}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-200 text-gray-700 font-medium rounded-xl hover:bg-gray-300 transition-colors"
              title="Reset"
            >
              <RotateCcw className="w-5 h-5" />
              Reset
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageWatermark;
